import { AxiosInstance, InternalAxiosRequestConfig } from "axios";
import { keycloakService } from "@Services/keycloak";

/**
 * TokenRefreshService (Singleton)
 *
 * Centraliza a lógica de refresh de token para evitar múltiplas chamadas
 * concorrentes de refresh quando várias requisições recebem 401 ao mesmo tempo.
 */
class TokenRefreshService {
  private static instance: TokenRefreshService;
  private isRefreshing = false;
  private refreshSubscribers: Array<(token: string | null) => void> = [];
  private sessionExpiredCallback: (() => void) | null = null;

  private constructor() {}

  setSessionExpiredCallback(callback: () => void): void {
    this.sessionExpiredCallback = callback;
  }

  private subscribeTokenRefresh(cb: (token: string | null) => void): void {
    this.refreshSubscribers.push(cb);
  }

  private notifyRefreshSubscribers(token: string | null): void {
    this.refreshSubscribers.forEach((cb) => cb(token));
    this.refreshSubscribers = [];
  }

  async handle401Error(error: any, axiosInstance: AxiosInstance): Promise<any> {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // Prevent infinite retry loops
    if (originalRequest._retry) {
      if (this.sessionExpiredCallback) {
        this.sessionExpiredCallback();
      } else {
        keycloakService.logout();
      }
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    if (!this.isRefreshing) {
      this.isRefreshing = true;

      keycloakService
        .refreshToken()
        .then(() => {
          const newToken = keycloakService.getAccessToken();
          this.notifyRefreshSubscribers(newToken || null);
        })
        .catch(() => {
          this.notifyRefreshSubscribers(null);
          if (this.sessionExpiredCallback) {
            this.sessionExpiredCallback();
          } else {
            keycloakService.logout();
          }
        })
        .finally(() => {
          this.isRefreshing = false;
        });
    }

    return new Promise((resolve, reject) => {
      this.subscribeTokenRefresh((token) => {
        if (token) {
          try {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            resolve(axiosInstance(originalRequest));
          } catch (e) {
            reject(e);
          }
        } else {
          reject(error);
        }
      });
    });
  }

  static getInstance(): TokenRefreshService {
    if (!TokenRefreshService.instance) {
      TokenRefreshService.instance = new TokenRefreshService();
    }
    return TokenRefreshService.instance;
  }
}

export const tokenRefreshService = TokenRefreshService.getInstance();
